import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { UiButton, VideoPlayer } from '@shared/ui';

import { ModalButtonList } from './Components/ModalButtonList/ModalButtonList';
import { useClickOutside } from './customHooks/useClickOutside';
import { useImageDirection } from './customHooks/useImageDirection';
import { useKeyPress } from './customHooks/useKeyPress';
import cls from './PrevMedia.module.css';
import { getNameExtension } from '../../../TemporaryConmonents/Files/helpers/getNameExtension';
import { isImg } from '../../../TemporaryConmonents/Files/helpers/isImg';
import { saveFile } from '../../../TemporaryConmonents/Files/helpers/saveFile';
import { Icon } from '../Icon/Icon';

interface PrevMediaProps {
    currentIndex: number;
    mediaDataList: { url: string; mimeType: string }[];
    setCurrentIndex: (index?: number) => void;
    setCurrentUrl: (url: string) => void;
    setCurrentMimeType?: (mimeType: string) => void;
    onHideModal: () => void;
    currentUrl: string;
    fileName: string;
    title?: string;
    isEditable?: boolean;
    isDeletion?: boolean;
    deleteImage?: () => void;
    renameImage?: (fileName?: string) => void;
    isImage?: boolean;
    isVideoFile?: boolean;
    isNotSaving?: boolean;
    description?: string;
}

export enum ModePrevModel {
    DELETE = 'delete',
    EDIT = 'edit',
}

export const PrevMedia: FunctionComponent<PrevMediaProps> = ({
    currentIndex,
    mediaDataList,
    setCurrentIndex,
    setCurrentUrl,
    setCurrentMimeType,
    onHideModal,
    currentUrl,
    fileName,
    title = null,
    isEditable = false,
    isDeletion = false,
    deleteImage,
    renameImage,
    isImage,
    isVideoFile,
    isNotSaving,
    description,
}) => {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const ref = useRef(null);
    const [mode, setMode] = useState<null | ModePrevModel>(null);
    const { isLast, isFirst, onClickPrev, onClickNext } = useImageDirection({
        ref,
        currentIndex,
        mediaDataList,
        setCurrentIndex,
        setCurrentUrl,
        setCurrentMimeType,
    });

    const { extension } = getNameExtension(fileName);
    const fileIsImg = isImg(extension);
    const isPrevFormat = !!fileIsImg || !!isImage || !!isVideoFile;
    const hideSecondModel = () => {
        if (mode === ModePrevModel.DELETE) {
            setMode(null);
        }
        setMode(null);
    };
    useClickOutside(ref, onHideModal, mode);
    useKeyPress({ targetKey: 'Escape', onHideModal, mode });

    useEffect(() => {
        console.log('fileName', fileName);
        console.log('currentUrl', currentUrl);
    }, [fileName, currentUrl]);

    return (
        <>
            <ModalButtonList
                mode={mode}
                onHideModal={hideSecondModel}
                deleteImage={deleteImage}
                renameImage={renameImage}
            />
            <div className={cls.customModal} ref={modalRef}>
                <div className={classNames(cls.customModalOverlay)}>
                    <div
                        className={cls.prevFileArrow}
                        ref={ref}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {!isFirst && (
                            <UiButton
                                variant="icon"
                                onClick={onClickPrev}
                                startIcon={<Icon iconName="arrowLeft" />}
                            />
                        )}
                    </div>
                    <div className={cls.nextFileArrow} onClick={(event) => event.stopPropagation()}>
                        {!isLast && (
                            <UiButton
                                variant="icon"
                                onClick={onClickNext}
                                startIcon={<Icon iconName="arrowRight" />}
                            />
                        )}
                    </div>
                    <div className={cls.prevHeader} onClick={(event) => event.stopPropagation()}>
                        <div className={cls.iconBack}>
                            <UiButton
                                variant="icon"
                                onClick={onHideModal}
                                className={cls.iconBackStart}
                                startIcon={<Icon iconName="back" />}
                            />
                        </div>
                        <div className={cls.prevHeaderName}>{title}</div>
                        <div className={cls.prevHeaderIcons}>
                            {!isNotSaving && (
                                <UiButton
                                    variant="icon"
                                    onClick={async () => {
                                        await saveFile(currentUrl, fileName);
                                    }}
                                    startIcon={<Icon iconName="download" />}
                                />
                            )}
                            {isEditable && (
                                <UiButton
                                    variant="icon"
                                    onClick={() => setMode(ModePrevModel.EDIT)}
                                    startIcon={<Icon iconName="edit" />}
                                />
                            )}
                            {isDeletion && (
                                <UiButton
                                    variant="icon"
                                    onClick={() => setMode(ModePrevModel.DELETE)}
                                    startIcon={<Icon iconName="delete" />}
                                />
                            )}
                        </div>
                    </div>
                    {isPrevFormat ? (
                        <div
                            className={classNames(cls.prevFiles)}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {isVideoFile && (
                                <div className={cls.VideoContainer}>
                                    <VideoPlayer
                                        autoPlay
                                        controls
                                        style={{
                                            // width: '100%',
                                            height: '100%',
                                            minWidth: '480px',
                                            minHeight: '270px',
                                            maxHeight: 'calc(100vh - 200px)',
                                        }}
                                        videoSrc={currentUrl}
                                    />
                                    {description && (
                                        <div
                                            className={classNames(
                                                cls.description,
                                                cls.videoDescription
                                            )}
                                        >
                                            {description}
                                        </div>
                                    )}
                                </div>
                            )}

                            {isImage && (
                                <div className={cls.ImageContainer}>
                                    <img className={cls.imgModal} src={currentUrl} alt={fileName} />
                                    {description && (
                                        <div className={cls.description}>{description}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            className={classNames(cls.customModalContent)}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className={cls.notPrevFormatBody}>
                                {t('PrevImage.return.notPrevFormatBody')}
                            </div>
                            <div className={cls.notPrevFormatFooter}>
                                <UiButton
                                    onClick={async () => {
                                        await saveFile(currentUrl, fileName);
                                    }}
                                    startIcon={<Icon iconName="download" />}
                                    btnText={t('PrevImage.return.notPrevFormatFooter.button')}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
